import useStyles from 'isomorphic-style-loader/useStyles';
import { PencilOff } from 'lucide-react';
import { useController, type FieldValues } from 'react-hook-form';
import { Form, TextArea, type TextAreaProps } from 'semantic-ui-react';

import { CharacterCounter } from './character-counter';
import { FieldError } from './field-error';
import type { FieldProps, WithLimit } from './field.types';
import s from './field-textarea.scss';

export type FieldTextareaProps<T extends FieldValues = FieldValues> =
  FieldProps<T, TextAreaProps, 'value'> & WithLimit;

/**
 * Textarea field wired to React Hook Form via `useController`.
 *
 * Wraps SUI `TextArea` inside a `Form.Field` and handles the SUI `onChange`
 * unwrapping, error display, and `aria-errormessage` wiring automatically.
 *
 * All additional props are forwarded to the underlying SUI `TextArea`.
 */
export const FieldTextarea = <T extends FieldValues>({
  className,
  control,
  disabled,
  label,
  limit,
  name,
  required,
  width,
  ...rest
}: FieldTextareaProps<T>) => {
  useStyles(s);

  const { field, fieldState } = useController({ control, name });
  const id = rest.id ?? name;
  const errorId = `${id}-error`;
  const hasError = !!fieldState.error;
  const hasCounter = typeof limit === 'number' && limit >= 0;

  return (
    <Form.Field
      className={className}
      error={hasError}
      required={required}
      width={width}
    >
      {label && <label htmlFor={id}>{label}</label>}

      <div className={s.wrapper}>
        <TextArea
          {...rest}
          ref={field.ref}
          aria-errormessage={hasError ? errorId : undefined}
          aria-invalid={hasError || undefined}
          disabled={disabled}
          id={id}
          name={field.name}
          onBlur={field.onBlur}
          onChange={(_, { value }) => field.onChange(value)}
          value={field.value ?? ''}
        />

        {disabled && <PencilOff className={s.disabledIcon} />}

        {hasCounter && (
          <CharacterCounter control={control} limit={limit} name={name} />
        )}
      </div>

      <FieldError id={errorId} message={fieldState.error?.message} />
    </Form.Field>
  );
};
